import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { RootState } from '../reducers';
import { addTagToUserThunk } from '../features/settings/settingsSlice';

// Define the feature flag schema
export const featureFlagSchema = z.object({
  feature_enable_onboarding: z.enum(['true', 'false']),
  feature_unguard_upload: z.enum(['true', 'false']),
  feature_unguard_import_url: z.enum(['true', 'false']),
  feature_support_variant_ai: z.enum(['true', 'false']), // Currently disabled - using team tags instead
  feature_hide_unlimited: z.enum(['true', 'false']),
  feature_chevron_below_price: z.enum(['true', 'false']),
  feature_hide_upgrade_left_column: z.enum(['true', 'false']),
  feature_higher_free_limits: z.enum(['true', 'false']),
  feature_show_compare_plans: z.enum(['true', 'false']),
  feature_show_trial_modal: z.enum(['true', 'false']), // Enabled for all users
  feature_7_day_trial: z.enum(['true', 'false']), // Enabled for all users - 7-day trial
  // Onboarding A/B test - 4 mutually exclusive groups (25% each):
  // Group 1: feature_show_signup_onboarding=true - OB survey + trial modal immediately
  // Group 2: Control - all false, regular trial modal
  // Group 3: feature_ob_tooltips=true - OB survey + tooltip based on feature interest + upgrade modal on click
  // Group 4: feature_deferred_upgrade_modal=true - OB survey + dashboard + upgrade modal on high-value action
  feature_show_signup_onboarding: z.enum(['true', 'false']),
  feature_ob_tooltips: z.enum(['true', 'false']),
  feature_deferred_upgrade_modal: z.enum(['true', 'false']),
});

// Derive TypeScript types from Zod schema
export type FeatureFlagKey = keyof z.infer<typeof featureFlagSchema>;
export type FeatureFlagValue = z.infer<
  typeof featureFlagSchema
>[FeatureFlagKey];

// Helper function to generate random boolean
const getRandomBoolean = () => Math.random() >= 0.5;

// Helper function to check if a key is a feature flag
const isFeatureFlag = (key: string): key is FeatureFlagKey => {
  return key.startsWith('feature_');
};

// Cache for pending feature flag assignments to prevent race conditions
// This ensures consistent values even if component re-renders before Redux updates
const pendingFlagAssignments = new Map<string, boolean>();

export const useBooleanFeature = () => {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.settings.user.tags);

  const getFeature = useCallback(
    (key: string): boolean => {
      // Fixed feature flags - these are hardcoded values
      if (key === 'feature_enable_onboarding') {
        return false;
      }

      if (key === 'feature_hide_unlimited') {
        return false;
      }

      if (key === 'feature_unguard_upload') {
        return false;
      }

      if (key === 'feature_unguard_import_url') {
        return false;
      }

      if (key === 'feature_higher_free_limits') {
        return false;
      }

      if (key === 'feature_chevron_below_price') {
        return false;
      }

      if (key === 'feature_hide_upgrade_left_column') {
        return false;
      }

      if (key === 'feature_show_compare_plans') {
        return false;
      }

      if (key === 'feature_show_trial_modal') {
        return true;
      }

      if (key === 'feature_7_day_trial') {
        return true;
      }

      // Onboarding experiment - 4 mutually exclusive groups (25% each)
      // All 3 flags are assigned together to ensure mutual exclusivity
      if (
        key === 'feature_show_signup_onboarding' ||
        key === 'feature_ob_tooltips' ||
        key === 'feature_deferred_upgrade_modal'
      ) {
        // Check if user already has ANY of the onboarding flags assigned
        const hasExistingAssignment =
          'feature_show_signup_onboarding' in tags ||
          'feature_ob_tooltips' in tags ||
          'feature_deferred_upgrade_modal' in tags;

        if (hasExistingAssignment) {
          // User already assigned - return their value for this specific flag
          pendingFlagAssignments.delete(key);
          return tags[key] === 'true';
        }

        // Check pending cache to prevent race conditions during rapid re-renders
        if (pendingFlagAssignments.has('feature_show_signup_onboarding')) {
          return pendingFlagAssignments.get(key) || false;
        }

        // First time assignment - randomly assign to one of 4 groups
        const randomValue = Math.random();
        let group1 = false; // feature_show_signup_onboarding - OB + trial modal
        let group3 = false; // feature_ob_tooltips - OB + tooltips
        let group4 = false; // feature_deferred_upgrade_modal - OB + deferred modal
        // Group 2 (control) = all false

        if (randomValue < 0.25) {
          group1 = true; // 0-25%: Original OB flow
        } else if (randomValue < 0.5) {
          // 25-50%: Control group - all false
        } else if (randomValue < 0.75) {
          group3 = true; // 50-75%: OB + tooltips
        } else {
          group4 = true; // 75-100%: OB + deferred upgrade modal
        }

        // Cache all values to ensure consistency before Redux updates
        pendingFlagAssignments.set('feature_show_signup_onboarding', group1);
        pendingFlagAssignments.set('feature_ob_tooltips', group3);
        pendingFlagAssignments.set('feature_deferred_upgrade_modal', group4);

        // Dispatch all flag assignments together
        dispatch(
          addTagToUserThunk({
            key: 'feature_show_signup_onboarding',
            value: group1.toString() as FeatureFlagValue,
          }),
        );
        dispatch(
          addTagToUserThunk({
            key: 'feature_ob_tooltips',
            value: group3.toString() as FeatureFlagValue,
          }),
        );
        dispatch(
          addTagToUserThunk({
            key: 'feature_deferred_upgrade_modal',
            value: group4.toString() as FeatureFlagValue,
          }),
        );

        // Return the value for the requested key
        if (key === 'feature_show_signup_onboarding') return group1;
        if (key === 'feature_ob_tooltips') return group3;
        if (key === 'feature_deferred_upgrade_modal') return group4;
        return false;
      }

      // If it's a defined feature flag
      if (isFeatureFlag(key)) {
        // If the feature flag exists in tags, return its value
        if (key in tags) {
          // Clean up pending cache once value is in Redux
          pendingFlagAssignments.delete(key);
          return tags[key] === 'true';
        }

        // Check pending cache to prevent race conditions during rapid re-renders
        if (pendingFlagAssignments.has(key)) {
          return pendingFlagAssignments.get(key)!;
        }

        // If the feature flag doesn't exist, calculate a random value and set it
        const randomValue = getRandomBoolean();

        // Cache the value to ensure consistency before Redux updates
        pendingFlagAssignments.set(key, randomValue);

        const stringValue = randomValue.toString() as FeatureFlagValue;
        dispatch(addTagToUserThunk({ key, value: stringValue }));
        return randomValue;
      }

      // For undefined feature flags, return random boolean without storing
      return getRandomBoolean();
    },
    [dispatch, tags],
  );

  return { getFeature };
};

export default useBooleanFeature;
